import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';

import { hooks as individualHooks } from '@/domains/individual';

import SectionTitle from '../SectionTitle/SectionTitle';
import TextBlock from '../TextBlock/TextBlock';
import Button from '../Button/Button';
import InputAutoComplete from '../InputAutoComplete/InputAutoComplete';
import CountryTable from '../TravelCountryTable/CountryTable';
import WidgetWrapper from '../../widget-wrapper/WidgetWrapper';

import styles from './SearchTravel.module.scss';

const SearchTravel = ({ data }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const [value, setValue] = useState('');
  const [id, setId] = useState(null);
  const lang = router?.locale || 'az';

  const pageType = router.pathname.startsWith('/business')
    ? 'business'
    : 'individual';

  const idRef = useRef(null);

  useEffect(() => {
    if (id) {
      idRef.current = id;
    }
  }, [id]);

  useEffect(() => {
    if (router.isReady && router.query.country) {
      setValue(router.query.country);

      setId(
        data?.countries?.filter(
          (item) => item?.name === router.query.country,
        )[0]?.id,
      );
    }
  }, [router.isReady, router.query, router.locale, data?.countries]);

  useEffect(() => {
    setValue('');
  }, [router.locale]);

  const { data: list } = individualHooks.useGetCountries({
    widgetId: data?.id,
    id: id || idRef.current,
    pageType,
    lang,
  });

  const filteredCountries = data?.countries?.filter((item) =>
    item?.name?.toLowerCase().includes(value?.toLowerCase()),
  );

  const search = (event) => {
    event.preventDefault();
    if (!value || !filteredCountries.length) return;
    router.push(
      {
        query: {
          ...router.query,
          country: value,
        },
      },
      undefined,
      { scroll: false },
    );
  };

  const queryHandler = (value) => {
    router.push(
      {
        query: {
          ...router.query,
          country: value,
        },
      },
      undefined,
      { scroll: false },
    );
  };

  const renderPopularCountries = (item, index) => {
    return (
      <div
        key={index}
        className={styles.bottom_country_box}
        onClick={() => queryHandler(item?.name)}
      >
        {item?.name}
      </div>
    );
  };

  const shouldRenderCountryTable =
    !!list && !!router.query.country?.trim() && filteredCountries.length > 0;

  return (
    <>
      <div
        className={classNames('wrapper little-margin')}
        itemScope
        itemType='https://schema.org/Service'
      >
        <section
          className={styles.travel}
          style={{
            backgroundImage: `url("${data?.background_image}")`,
          }}
        >
          <div className={styles.travel__desc}>
            {data?.title && (
              <SectionTitle
                titleModify={classNames('title--large', styles.travel__title)}
              >
                {data?.title}
              </SectionTitle>
            )}
            {data?.description && (
              <TextBlock
                className={classNames(styles.travel__text, 'text--large')}
              >
                {data?.description}
              </TextBlock>
            )}
          </div>
          <div className={styles.travel__content}>
            <form className={styles.travel__form} onSubmit={search}>
              <InputAutoComplete
                label={data?.input_label}
                setValue={setValue}
                value={value}
                data={data}
                classname={styles.travel__input}
                placeholder={t('common:Enter Country')}
                withoutPopulars
              />
              <Button className={styles.travel__button} type={'submit'}>
                {data?.button_text}
              </Button>
            </form>
            <div className={styles.travel__popular}>
              {data?.bottom_countries_title && (
                <span className={styles['travel__popular-label']}>
                  {data?.bottom_countries_title}
                </span>
              )}
              {!!data?.bottom_countries && (
                <div className={styles['travel__popular-list']}>
                  {data.bottom_countries.map(renderPopularCountries)}
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
      {shouldRenderCountryTable && <CountryTable table={data} data={list} />}
      {!!list && !!list.widgets && (
        <div className='wrapper'>
          <WidgetWrapper widgets={list.widgets} />
        </div>
      )}
    </>
  );
};

export default SearchTravel;
